import type { IsoWeekday } from '@pulsify/shared/dto'

const A_MONDAY = Date.UTC(2024, 0, 1)
const DAY_MS = 86_400_000

export function weekdayLabel(
  weekday: IsoWeekday,
  locale: string,
  width: 'short' | 'long' = 'short',
): string {
  return new Intl.DateTimeFormat(locale, { weekday: width, timeZone: 'UTC' }).format(
    new Date(A_MONDAY + (weekday - 1) * DAY_MS),
  )
}

/** "Mo–Fr", "Mo, Mi, Fr" or "Mo–Mi, Fr": runs of three or more days collapse into a range. */
export function formatWeekdays(weekdays: readonly IsoWeekday[], locale: string): string {
  const runs: IsoWeekday[][] = []
  for (const weekday of [...new Set(weekdays)].sort((a, b) => a - b)) {
    const run = runs.at(-1)
    if (run && run[run.length - 1] === weekday - 1) run.push(weekday)
    else runs.push([weekday])
  }
  return runs
    .flatMap((run) =>
      run.length >= 3
        ? [`${weekdayLabel(run[0], locale)}–${weekdayLabel(run[run.length - 1], locale)}`]
        : run.map((weekday) => weekdayLabel(weekday, locale)),
    )
    .join(', ')
}

/** "Montag, 13.10." in German, "Monday, Oct 13" in English. */
export function formatMonday(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: locale.startsWith('de') ? 'numeric' : 'short',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`))
}

export function formatUpdatedAt(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Vienna',
  }).format(new Date(iso))
}
